import React, { useState, useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Polygon, Popup, Tooltip, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import {
  generateHeatmap,
  getHeatmap,
  getActivityColor,
  getActivityOpacity,
  getResolutionDescription,
  formatDwellTime,
} from '../../../services/heatmap.service';

/**
 * BehavioralHeatmap - Leaflet map showing H3 hexagonal activity heatmap
 */
const BehavioralHeatmap = ({ alienId, height = 400 }) => {
  const [heatmapData, setHeatmapData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [resolution, setResolution] = useState(8);
  const [days, setDays] = useState(30);
  const [selectedCell, setSelectedCell] = useState(null);

  // Calculate max score for color scaling
  const maxScore = useMemo(() => {
    if (!heatmapData?.cells?.length) return 1;
    return Math.max(...heatmapData.cells.map(c => c.timeWeightedScore));
  }, [heatmapData]);

  // Fetch or generate heatmap data
  const fetchHeatmap = async (forceRegenerate = false) => {
    if (!alienId) return;

    try {
      setLoading(true);
      setError(null);

      let data = null;

      // Try to get cached heatmap first (unless force regenerate)
      if (!forceRegenerate) {
        data = await getHeatmap(alienId, resolution);
      }

      // If no cached data or regenerating, generate new heatmap
      if (!data) {
        setGenerating(true);
        data = await generateHeatmap(alienId, resolution, days);
        setGenerating(false);
      }

      setHeatmapData(data);
    } catch (err) {
      console.error('Error fetching heatmap:', err);
      setError('Failed to load heatmap data');
    } finally {
      setLoading(false);
      setGenerating(false);
    }
  };

  useEffect(() => {
    fetchHeatmap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alienId]);

  const handleRegenerateClick = () => {
    fetchHeatmap(true);
  };

  const handleResolutionChange = (e) => {
    const newRes = parseInt(e.target.value, 10);
    setResolution(newRes);
  };

  const handleDaysChange = (e) => {
    const newDays = parseInt(e.target.value, 10);
    setDays(newDays);
  };

  // Re-generate when resolution or days change
  useEffect(() => {
    if (!loading && alienId) {
      fetchHeatmap(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolution, days]);

  if (!alienId) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Behavioral Heatmap</h3>
        <div className="bg-gray-100 rounded-lg flex items-center justify-center text-gray-500" style={{ height }}>
          No participant selected
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Behavioral Heatmap</h3>
        <div className="bg-gray-100 rounded-lg flex items-center justify-center" style={{ height }}>
          <div className="text-gray-500">
            {generating ? 'Generating heatmap...' : 'Loading...'}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Behavioral Heatmap</h3>
        <div className="bg-gray-100 rounded-lg flex items-center justify-center text-red-500" style={{ height }}>
          {error}
        </div>
      </div>
    );
  }

  // Calculate map center from cells
  const getMapCenter = () => {
    if (heatmapData?.cells?.length > 0) {
      // Center on highest activity cell
      const topCell = heatmapData.cells[0];
      return [topCell.cellCenterLat, topCell.cellCenterLon];
    }
    // Default to US center
    return [39.8283, -98.5795];
  };

  const mapCenter = getMapCenter();
  const cells = heatmapData?.cells || [];

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-700">Behavioral Heatmap</h3>
        <div className="flex items-center gap-3">
          {/* Resolution selector */}
          <div className="flex items-center gap-1">
            <label className="text-xs text-gray-500">Resolution:</label>
            <select
              value={resolution}
              onChange={handleResolutionChange}
              className="text-xs border border-gray-300 rounded px-1 py-0.5"
            >
              <option value={7}>7 (~5km)</option>
              <option value={8}>8 (~1km)</option>
              <option value={9}>9 (~350m)</option>
              <option value={10}>10 (~100m)</option>
            </select>
          </div>

          {/* Days selector */}
          <div className="flex items-center gap-1">
            <label className="text-xs text-gray-500">Days:</label>
            <select
              value={days}
              onChange={handleDaysChange}
              className="text-xs border border-gray-300 rounded px-1 py-0.5"
            >
              <option value={7}>7</option>
              <option value={14}>14</option>
              <option value={30}>30</option>
              <option value={60}>60</option>
              <option value={90}>90</option>
            </select>
          </div>

          {/* Regenerate button */}
          <button
            onClick={handleRegenerateClick}
            disabled={generating}
            className="text-xs bg-blue-500 hover:bg-blue-600 text-white px-2 py-1 rounded disabled:opacity-50"
          >
            {generating ? 'Generating...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Summary stats */}
      {heatmapData?.summary && (
        <div className="flex items-center gap-4 mb-3 text-xs text-gray-600">
          <span>
            <strong>{heatmapData.summary.totalCells}</strong> cells
          </span>
          <span>
            <strong>{heatmapData.summary.totalActivityPoints}</strong> check-ins
          </span>
          <span>
            <strong>{heatmapData.summary.hotspotCells}</strong> hotspots
          </span>
        </div>
      )}

      {/* Map */}
      {cells.length > 0 ? (
        <div className="rounded-lg overflow-hidden" style={{ height }}>
          <MapContainer
            center={mapCenter}
            zoom={resolution >= 9 ? 14 : resolution === 8 ? 12 : 10}
            style={{ height: '100%', width: '100%' }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            {/* Fit map to show all cells */}
            <FitBoundsToHexes cells={cells} />

            {/* Render hexagonal cells */}
            {cells.map(cell => (
              <HexCell
                key={cell.gridCellId}
                cell={cell}
                maxScore={maxScore}
                isSelected={selectedCell === cell.gridCellId}
                onSelect={() => setSelectedCell(cell.gridCellId)}
              />
            ))}
          </MapContainer>
        </div>
      ) : (
        <div
          className="bg-gray-100 rounded-lg flex items-center justify-center text-gray-500"
          style={{ height }}
        >
          No location data available for the selected time range
        </div>
      )}

      {/* Legend */}
      <div className="mt-3 pt-3 border-t border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h4 className="text-xs font-semibold text-gray-600">Activity Level</h4>
            <div className="flex items-center h-3">
              <div
                className="w-20 h-full rounded"
                style={{
                  background: 'linear-gradient(to right, #3b82f6, #eab308, #ef4444)',
                }}
              />
            </div>
            <span className="text-xs text-gray-500">Low to High</span>
          </div>
          <div className="text-xs text-gray-400">
            {getResolutionDescription(resolution)}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Component to fit map bounds to hexagonal cells
 * Focuses on the main activity cluster (cells near the highest-activity cell)
 */
const FitBoundsToHexes = ({ cells }) => {
  const map = useMap();

  useEffect(() => {
    if (cells.length === 0) return;

    // Sort cells by activity to find the primary hotspot
    const sortedCells = [...cells].sort((a, b) => b.timeWeightedScore - a.timeWeightedScore);
    const topCell = sortedCells[0];
    const topLat = topCell.cellCenterLat;
    const topLon = topCell.cellCenterLon;

    // Filter to cells within ~100km of the top cell (roughly 1 degree lat/lon)
    // This prevents showing distant outliers that cause extreme zoom out
    const nearbyCells = cells.filter(cell => {
      const latDiff = Math.abs(cell.cellCenterLat - topLat);
      const lonDiff = Math.abs(cell.cellCenterLon - topLon);
      return latDiff < 1 && lonDiff < 1; // Within ~100km
    });

    // Use nearby cells if we have enough, otherwise use top 80% by score
    const cellsToFit = nearbyCells.length >= 2
      ? nearbyCells
      : sortedCells.slice(0, Math.max(2, Math.ceil(cells.length * 0.8)));

    // Get all polygon points from filtered cells
    const allPoints = cellsToFit.flatMap(cell =>
      cell.polygon.map(([lat, lon]) => [lat, lon])
    );

    if (allPoints.length > 0) {
      const lats = allPoints.map(p => p[0]);
      const lons = allPoints.map(p => p[1]);

      const bounds = [
        [Math.min(...lats), Math.min(...lons)],
        [Math.max(...lats), Math.max(...lons)],
      ];

      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
    }
  }, [cells, map]);

  return null;
};

/**
 * Hexagonal cell component
 */
const HexCell = ({ cell, maxScore, isSelected, onSelect }) => {
  if (!cell.polygon || cell.polygon.length < 3) {
    return null;
  }

  // Polygon positions are already [lat, lon] from the backend
  const positions = cell.polygon;
  const color = getActivityColor(cell.timeWeightedScore, maxScore);
  const opacity = getActivityOpacity(cell.timeWeightedScore, maxScore);

  return (
    <Polygon
      positions={positions}
      pathOptions={{
        color: color,
        fillColor: color,
        fillOpacity: isSelected ? 0.9 : opacity,
        weight: isSelected ? 2 : 1,
      }}
      eventHandlers={{
        click: onSelect,
      }}
    >
      <Tooltip direction="top" offset={[0, -10]}>
        <div className="text-xs">
          <div className="font-medium">{cell.activityCount} check-ins</div>
          <div className="text-gray-500">{cell.visitCount} visits</div>
        </div>
      </Tooltip>
      <Popup>
        <div className="p-1 min-w-[160px]">
          <h4 className="font-medium text-sm mb-2">Activity Cell</h4>
          <div className="space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-gray-500">Check-ins:</span>
              <span className="font-medium">{cell.activityCount}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Visits:</span>
              <span className="font-medium">{cell.visitCount}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Total dwell:</span>
              <span className="font-medium">{formatDwellTime(cell.dwellMinutesTotal)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Activity score:</span>
              <span className="font-medium">{cell.timeWeightedScore.toFixed(2)}</span>
            </div>
            {cell.firstSeen && (
              <div className="pt-1 mt-1 border-t border-gray-200">
                <div className="text-gray-500">First seen:</div>
                <div className="text-gray-700">
                  {new Date(cell.firstSeen).toLocaleDateString()}{' '}
                  {new Date(cell.firstSeen).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            )}
            {cell.lastSeen && (
              <div>
                <div className="text-gray-500">Last seen:</div>
                <div className="text-gray-700">
                  {new Date(cell.lastSeen).toLocaleDateString()}{' '}
                  {new Date(cell.lastSeen).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            )}
          </div>
          <div className="mt-2 pt-2 border-t border-gray-200 text-[10px] text-gray-400">
            H3: {cell.gridCellId.substring(0, 12)}...
          </div>
        </div>
      </Popup>
    </Polygon>
  );
};

export default BehavioralHeatmap;
